import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Save, Trash2 } from 'lucide-react';

import { followupApi, type FollowUpTemplate } from './api';
// PRD §10 — per-event templates need the live event list so the manager
// can scope a template to one of today's events instead of brand-wide.
import { eventsApi } from '@/features/events/api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * Caso 04 — Configure the post-event follow-up email per workspace. v1 lists
 * templates, lets you add/edit one. Mailgun sending kicks in once the infra
 * checklist (docs-pend/CHECKLIST-INFRA.md) is green; until then templates
 * are stored and dispatches are queued, no actual emails leave the system.
 */
export function FollowUpSettingsPage() {
  const t = useT();
  useFavoritable(t('followup.title'));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<FollowUpTemplate> | null>(null);
  /** La plantilla que se va a borrar. Sin `window.confirm`: ver `ConfirmDialog`. */
  const [aBorrar, setABorrar] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['followup', 'templates'],
    queryFn: () => followupApi.list(),
  });

  // PRD §10 — live events for the per-template event scope selector.
  // Brand-wide stays an explicit option ("All events"); event-scoped
  // templates win over brand-wide per the FollowUpService lookup.
  const { data: eventsList } = useQuery({
    queryKey: ['followup', 'events-for-scope'],
    queryFn: async () => (await eventsApi.listEvents()).items,
  });
  const eventNameById = new Map<number, string>(
    (eventsList ?? []).map((e) => [e.id, e.name ?? `Event ${e.id}`]),
  );

  const saveMutation = useMutation({
    mutationFn: (tmpl: Partial<FollowUpTemplate>) =>
      tmpl.id
        ? followupApi.update(tmpl.id, tmpl)
        : followupApi.create(tmpl),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['followup', 'templates'] });
      setEditing(null);
      toast({
        tone: 'success',
        title: t(
          vars.id ? 'followup.toast.updated' : 'followup.toast.created',
        ),
      });
    },
    onError: (e: any) =>
      toast({
        tone: 'error',
        title: t('followup.toast.saveError'),
        description: e?.message,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => followupApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followup', 'templates'] });
      toast({ tone: 'success', title: t('followup.toast.deleted') });
    },
    onError: (e: any) =>
      toast({
        tone: 'error',
        title: t('followup.toast.deleteError'),
        description: e?.message,
      }),
  });

  function blankTemplate(): Partial<FollowUpTemplate> {
    return {
      name: '',
      subject: t('followup.defaults.subject'),
      bodyHtml: t('followup.defaults.bodyHtml'),
      enabled: true,
      sendDelayHours: 0,
      xkEventId: null,
    };
  }

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="text-3xl font-bold">{t('followup.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('followup.subtitle')}
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('followup.templates.title')}
              </CardTitle>
              <CardDescription>
                {t('followup.templates.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(data ?? []).length === 0 ? (
                <p className="text-sm text-[var(--color-fg-muted)]">
                  {t('followup.templates.empty')}
                </p>
              ) : (
                <ul className="space-y-2">
                  {(data ?? []).map((tmpl) => (
                    <li
                      key={tmpl.id}
                      className="flex items-center justify-between rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3"
                    >
                      <div>
                        <div className="font-medium">{tmpl.name}</div>
                        <div className="text-xs text-[var(--color-fg-muted)]">
                          {tmpl.subject}
                        </div>
                        <div className="mt-1">
                          <Badge
                            tone={tmpl.xkEventId ? 'accent' : 'neutral'}
                          >
                            {tmpl.xkEventId
                              ? `${t('followup.scope.event')}: ${
                                  eventNameById.get(tmpl.xkEventId) ??
                                  `#${tmpl.xkEventId}`
                                }`
                              : t('followup.scope.brandWide')}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {tmpl.enabled ? (
                          <Badge tone="success">{t('followup.enabled')}</Badge>
                        ) : (
                          <Badge tone="neutral">{t('followup.disabled')}</Badge>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => setEditing(tmpl)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setABorrar(tmpl.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Button variant="primary" onClick={() => setEditing(blankTemplate())}>
                {t('followup.newTemplate')}
              </Button>
            </CardContent>
          </Card>

          {editing && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {editing.id
                    ? t('followup.editor.editTitle')
                    : t('followup.editor.newTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Field label={t('followup.editor.name')}>
                  <input
                    type="text"
                    value={editing.name ?? ''}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value })
                    }
                    className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
                  />
                  {!editing.name?.trim() && (
                    <p className="mt-1 text-[11px] text-[var(--color-warn)]">
                      {t('followup.editor.nameRequired')}
                    </p>
                  )}
                </Field>
                <Field label={t('followup.editor.scope')}>
                  <select
                    value={editing.xkEventId ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        xkEventId: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
                  >
                    <option value="">{t('followup.scope.brandWide')}</option>
                    {(eventsList ?? []).map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name ?? `Event ${e.id}`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-[var(--color-fg-muted)]">
                    {t('followup.editor.scopeHint')}
                  </p>
                </Field>
                <Field label={t('followup.editor.subject')}>
                  <input
                    type="text"
                    value={editing.subject ?? ''}
                    onChange={(e) =>
                      setEditing({ ...editing, subject: e.target.value })
                    }
                    className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
                  />
                </Field>
                <Field label={t('followup.editor.body')}>
                  <textarea
                    value={editing.bodyHtml ?? ''}
                    onChange={(e) =>
                      setEditing({ ...editing, bodyHtml: e.target.value })
                    }
                    rows={10}
                    className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-[var(--color-fg-subtle)]">
                    {t('followup.editor.variables')}
                  </p>
                </Field>
                <Field label={t('followup.editor.delay')}>
                  <input
                    type="number"
                    min={0}
                    value={editing.sendDelayHours ?? 0}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        sendDelayHours: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="w-32 rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
                  />
                </Field>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="primary"
                    onClick={() => saveMutation.mutate(editing)}
                    disabled={
                      saveMutation.isPending ||
                      !editing.name ||
                      !editing.subject ||
                      !editing.bodyHtml
                    }
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {t('common.save')}
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(null)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <ConfirmDialog
        open={aBorrar != null}
        onOpenChange={(v) => !v && setABorrar(null)}
        tone="danger"
        title={t('common.delete')}
        description={t('followup.confirmDelete')}
        confirmLabel={t('common.delete')}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (aBorrar != null) deleteMutation.mutate(aBorrar);
          setABorrar(null);
        }}
      />
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase text-[var(--color-fg-muted)]">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
